// src/components/discover/TasteCalibration.tsx
//
// Cold-start screen shown the first time a user lands on Discover
// without any taste signals. They tap interest chips that resonate;
// each tap seeds a synthetic positive signal so the Discover feed
// has SOMETHING to personalize on for their first session.
// Otherwise day-one Discover serves generic content and feels
// indistinguishable from a tourism brochure.
//
// Show conditions (decided by the parent page, not by this
// component):
//   1. User has NO taste calibration record yet AND
//   2. Discover feed has loaded items (so they have a deck behind
//      this overlay, not just empty)
//
// The user can either:
//   - Select chips and tap "Show me" → fires calibrateTaste with
//     the selected tags, marks calibration as done, dismisses
//   - Tap "Skip" → fires calibrateTaste with skipped=true, marks
//     done, dismisses (we never re-prompt)

import React, { useMemo, useState } from 'react';
import {
  Dialog,
  DialogContent,
  Box,
  Typography,
  Button,
  Chip,
  Stack,
  Alert,
  CircularProgress,
} from '@mui/material';
import { auth } from '../../services/firebase';
import { useT, TranslateFn } from '../../i18n/useT';

const FUNCTION_URL =
  'https://us-central1-travelx-46b7a.cloudfunctions.net/calibrateTaste';

interface CalibrationChip {
  tag: string;
  emoji: string;
}

/** 18 chips covering the major axes a travel taste profile lives
 *  on. Tags here are the SAME tag fragments the Guide / Discover
 *  recommendation backends already check against, so seeding these
 *  actually moves the needle on what gets surfaced.
 *
 *  Visible labels are NOT stored here — they live in `chipLabel()`
 *  below as literal `t("...")` calls so the i18n static extractor
 *  (scripts/extract-strings.mjs) can find them. Keeping the English
 *  source in a data field would be invisible to extraction. */
const CALIBRATION_CHIPS: CalibrationChip[] = [
  { tag: 'museum',           emoji: '🏛️' },
  { tag: 'art-gallery',      emoji: '🎨' },
  { tag: 'live-music',       emoji: '🎷' },
  { tag: 'street-food',      emoji: '🌮' },
  { tag: 'cafe',             emoji: '☕' },
  { tag: 'cocktail-bar',     emoji: '🍸' },
  { tag: 'wine-bar',         emoji: '🍷' },
  { tag: 'bakery',           emoji: '🥐' },
  { tag: 'bookstore',        emoji: '📚' },
  { tag: 'market',           emoji: '🛍️' },
  { tag: 'park',             emoji: '🌳' },
  { tag: 'viewpoint',        emoji: '⛰️' },
  { tag: 'historic',         emoji: '🏰' },
  { tag: 'architecture',     emoji: '🏛' },
  { tag: 'religious',        emoji: '⛪' },
  { tag: 'modern-art',       emoji: '🖼️' },
  { tag: 'hidden-gem',       emoji: '💎' },
  { tag: 'people-watching',  emoji: '👀' },
];

/** Translated visible label for a calibration chip. Each branch
 *  passes a STATIC string literal to t() so the extractor and the
 *  no-bare-strings ESLint rule both see them — the constraint is
 *  documented in src/i18n/useT.ts. The default branch returns the
 *  raw tag, which only fires on a typo'd CALIBRATION_CHIPS entry. */
function chipLabel(tag: string, t: TranslateFn): string {
  switch (tag) {
    case 'museum':           return t('Museums');
    case 'art-gallery':      return t('Art galleries');
    case 'live-music':       return t('Live music');
    case 'street-food':      return t('Street food');
    case 'cafe':             return t('Coffee shops');
    case 'cocktail-bar':     return t('Cocktail bars');
    case 'wine-bar':         return t('Wine bars');
    case 'bakery':           return t('Bakeries');
    case 'bookstore':        return t('Bookshops');
    case 'market':           return t('Markets');
    case 'park':             return t('Parks & gardens');
    case 'viewpoint':        return t('Views & vistas');
    case 'historic':         return t('Historic places');
    case 'architecture':     return t('Architecture');
    case 'religious':        return t('Religious sites');
    case 'modern-art':       return t('Modern art');
    case 'hidden-gem':       return t('Hidden gems');
    case 'people-watching':  return t('People-watching');
    default:                 return tag;
  }
}

interface Props {
  open: boolean;
  onComplete: () => void;
}

export const TasteCalibration: React.FC<Props> = ({ open, onComplete }) => {
  const t = useT();
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const toggle = (tag: string) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(tag)) {
        next.delete(tag);
      } else {
        next.add(tag);
      }
      return next;
    });
  };

  const submit = async (skipped: boolean) => {
    const user = auth.currentUser;
    if (!user) {
      // No user signed in (anonymous flow). Nothing to persist;
      // silently dismiss. Calibration only matters for logged-in
      // users with a /users/{uid} doc.
      onComplete();
      return;
    }
    setSubmitting(true);
    setError(null);
    try {
      const token = await user.getIdToken();
      const resp = await fetch(FUNCTION_URL, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          auth_token: token,
          selected_tags: skipped ? [] : Array.from(selected),
          skipped,
        }),
      });
      if (!resp.ok) {
        const txt = await resp.text().catch(() => '');
        throw new Error(`HTTP ${resp.status}: ${txt.slice(0, 160)}`);
      }
      onComplete();
    } catch (err) {
      const msg = err instanceof Error ? err.message : 'Unknown error';
      console.warn('calibrateTaste failed:', msg);
      setError(msg);
    } finally {
      setSubmitting(false);
    }
  };

  const minToProceed = 3;
  const canProceed = selected.size >= minToProceed;

  // Helpful counter copy: "3 more" / "1 more" / "ready" so the user
  // knows when they've crossed the minimum. Each branch passes a
  // static literal to t() (interpolation handles the numbers) so
  // the extractor sees all three variants.
  const counterText = useMemo(() => {
    if (selected.size === 0) {
      return t("Tap a few that sound like you ({n} or more)", {
        n: minToProceed,
      });
    }
    if (selected.size < minToProceed) {
      const left = minToProceed - selected.size;
      return t("{n} more to go", { n: left });
    }
    return t("{n} selected", { n: selected.size });
  }, [selected, t]);

  return (
    <Dialog
      open={open}
      onClose={undefined}
      fullWidth
      maxWidth="sm"
      PaperProps={{ sx: { borderRadius: 2, m: 2 } }}
    >
      <DialogContent sx={{ p: 3 }}>
        <Box sx={{ mb: 2 }}>
          <Typography variant="h5" sx={{ fontWeight: 700, mb: 0.5 }}>
            
            {t("What sounds like you?")}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            
            {t("Tap a few interests so Discover knows where to start. You can always change this by swiping on cards.")}
          </Typography>
        </Box>

        <Box
          sx={{
            display: 'flex',
            flexWrap: 'wrap',
            gap: 0.75,
            mb: 2,
          }}
        >
          {CALIBRATION_CHIPS.map((chip) => {
            const active = selected.has(chip.tag);
            return (
              <Chip
                key={chip.tag}
                label={`${chip.emoji} ${chipLabel(chip.tag, t)}`}
                clickable
                onClick={() => toggle(chip.tag)}
                variant={active ? 'filled' : 'outlined'}
                sx={{
                  fontWeight: 600,
                  fontSize: '0.85rem',
                  py: 2,
                  ...(active
                    ? {
                        bgcolor: 'primary.main',
                        color: 'primary.contrastText',
                        '&:hover': { bgcolor: 'primary.dark' },
                      }
                    : {}),
                }}
              />
            );
          })}
        </Box>

        <Typography
          variant="caption"
          color={canProceed ? 'success.main' : 'text.secondary'}
          sx={{ display: 'block', mb: 2, fontWeight: 600 }}
        >
          {counterText}
        </Typography>

        {error && (
          <Alert severity="warning" sx={{ mb: 2 }}>
            {error}  {t("(we'll try again)")}
          </Alert>
        )}

        <Stack direction="row" spacing={1} justifyContent="space-between">
          <Button
            variant="text"
            onClick={() => submit(true)}
            disabled={submitting}
            sx={{ textTransform: 'none' }}
          >
            
            {t("Skip")}
          </Button>
          <Button
            variant="contained"
            disabled={!canProceed || submitting}
            onClick={() => submit(false)}
            startIcon={
              submitting ? <CircularProgress size={16} color="inherit" /> : null
            }
            sx={{ textTransform: 'none', minWidth: 120 }}
          >
            {submitting ? t("Saving…") : t("Show me")}
          </Button>
        </Stack>
      </DialogContent>
    </Dialog>
  );
};
